/**
 * Skickbedömning assessment groups.
 *
 * Three fixed, code-defined partitions of component base types. Each group has
 * a system building-check template with a fixed ID and the category
 * `condition_assessment`; a building gets one check instance per group with
 * live components. There is no such grouping in the data model: it is derived
 * from `BaseType` plus, for doors and locks, the door category and the parent
 * surface. An Övrigt component has no group of its own: it follows the surface
 * it is mounted on. Fast inredning is assessed with the indoor surfaces once it
 * has a room; an unplaced one ("Utan rum") is not assessed.
 *
 * SYNC: ios/Husappen/Building/Reassessment/ReassessmentGroup.swift
 * (`AssessmentGroup`). The sync is on membership, not enum names: web has
 * `Structural` + `GeoTechnics`, iOS has five `structural_*` cases, all "not
 * assessed". Change both or neither.
 */
import { BaseType, BuildComponentTypeDefinition } from '../types/Enums';
import type { BaseComponent, Building } from '../types/protocols';
import { addInterval as addIntervalToDate, type DateInterval } from './DateUtils';

export type AssessmentGroupKey = 'roofAndDrainage' | 'facadeAndWindows' | 'surfaces';

export const ASSESSMENT_GROUP_KEYS: readonly AssessmentGroupKey[] = ['roofAndDrainage', 'facadeAndWindows', 'surfaces'];

export const isAssessmentGroupKey = (value: unknown): value is AssessmentGroupKey =>
    typeof value === 'string' && (ASSESSMENT_GROUP_KEYS as readonly string[]).includes(value);

export interface AssessmentGroupDefinition {
    key: AssessmentGroupKey;
    /** Swedish label, also the suffix of the system template's name. */
    label: string;
    /** Base types that belong to the group. Doors, locks and fast inredning (room required) are resolved per component. */
    baseTypes: readonly BaseType[];
    /** Read-only description of the membership for the templates editor. */
    membersDescription: string;
}

export const ASSESSMENT_GROUPS: Record<AssessmentGroupKey, AssessmentGroupDefinition> = {
    roofAndDrainage: {
        key: 'roofAndDrainage',
        label: 'Tak & avvattning',
        baseTypes: [BaseType.Roof, BaseType.RoofDrainage, BaseType.SheetMetal, BaseType.Chimney, BaseType.RoofSafety],
        membersDescription: 'Tak, takavvattning, kompletterande plåtarbeten, skorstenar, taksäkerhet och övrigt som sitter på tak'
    },
    facadeAndWindows: {
        key: 'facadeAndWindows',
        label: 'Fasad & fönster',
        baseTypes: [BaseType.Facade, BaseType.Window, BaseType.Balcony],
        membersDescription: 'Fasader, fönster, balkonger, ytterdörrar och deras lås samt övrigt som sitter på fasad'
    },
    surfaces: {
        key: 'surfaces',
        label: 'Ytskikt invändigt',
        baseTypes: [BaseType.Floor, BaseType.Ceiling, BaseType.Wall, BaseType.Membrane, BaseType.FixedFurnishing],
        membersDescription: 'Golv, innertak, väggar, tätskikt, fast inredning i rum, innerdörrar och deras lås samt övrigt som sitter på dessa ytor'
    }
};

/** Fixed template ID per group: `inspections/templates/conditionAssessment_{group}`. */
export const ASSESSMENT_TEMPLATE_ID_PREFIX = 'conditionAssessment_';

export const assessmentTemplateId = (group: AssessmentGroupKey): string =>
    `${ASSESSMENT_TEMPLATE_ID_PREFIX}${group}`;

export const assessmentTemplateName = (group: AssessmentGroupKey): string =>
    `Skickbedömning – ${ASSESSMENT_GROUPS[group].label}`;

/** Code default when a template is created and no legacy config exists. */
export const DEFAULT_ASSESSMENT_INTERVAL: DateInterval = { value: 3, unit: 'years' };

/** Value of `DoorCategory.exterior_door` (protocols); a literal to keep this module free of runtime imports from protocols. */
const EXTERIOR_DOOR_CATEGORY = 'exterior_door';

type ComponentMap = Record<string, BaseComponent>;
type TypeDefinitions = Record<string, BuildComponentTypeDefinition>;

/** A component is live when it is not archived. */
export const isLiveComponent = (component: BaseComponent | undefined | null): component is BaseComponent =>
    !!component && !component.archivedAt;

const liveParent = (key: string | null | undefined, components: ComponentMap): BaseComponent | null => {
    if (!key) return null;
    const parent = components[key];
    return isLiveComponent(parent) ? parent : null;
};

/**
 * Exterior when the door's type definition says so; otherwise when either parent
 * surface is a live facade or roof. Falls back to interior.
 */
export const isExteriorDoor = (
    door: BaseComponent,
    components: ComponentMap,
    typeDefinitions: TypeDefinitions
): boolean => {
    const typeKey = door.specificType?.value;
    const doorCategory = typeKey ? typeDefinitions[typeKey]?.doorProps?.doorCategory : undefined;
    if (doorCategory) {
        return doorCategory === EXTERIOR_DOOR_CATEGORY;
    }
    return [door.parentSurface1?.value, door.parentSurface2?.value].some(key => {
        const parent = liveParent(key, components);
        return parent?.type === BaseType.Facade || parent?.type === BaseType.Roof;
    });
};

/** Base types an Övrigt component can be mounted on (iOS `MiscellaneousModel.parentTypes`). */
const MISCELLANEOUS_PARENT_TYPES: readonly BaseType[] = [BaseType.Roof, BaseType.Facade, BaseType.Wall, BaseType.Ceiling, BaseType.Floor];

/**
 * The assessment group a component belongs to, or null when it is not assessed
 * (utility, outdoor, foundation, ground drainage, risers, geotechnics, structural,
 * an Övrigt that is not mounted on a live surface, and fast inredning without a room).
 */
export const resolveAssessmentGroup = (
    component: BaseComponent,
    components: ComponentMap,
    typeDefinitions: TypeDefinitions
): AssessmentGroupKey | null => {
    switch (component.type) {
        case BaseType.Roof:
        case BaseType.RoofDrainage:
        case BaseType.SheetMetal:
        case BaseType.Chimney:
        case BaseType.RoofSafety:
            return 'roofAndDrainage';
        case BaseType.Facade:
        case BaseType.Window:
        case BaseType.Balcony:
            return 'facadeAndWindows';
        case BaseType.Floor:
        case BaseType.Ceiling:
        case BaseType.Wall:
        case BaseType.Membrane:
            return 'surfaces';
        case BaseType.Door:
            return isExteriorDoor(component, components, typeDefinitions) ? 'facadeAndWindows' : 'surfaces';
        case BaseType.LockMechanism: {
            // A lock follows the door or window it is mounted on. A lock whose
            // parent is neither (bad data) is treated as interior, as on iOS.
            const parent = liveParent(component.parentKey?.value, components);
            if (parent && (parent.type === BaseType.Door || parent.type === BaseType.Window)) {
                return resolveAssessmentGroup(parent, components, typeDefinitions);
            }
            return 'surfaces';
        }
        case BaseType.FixedFurnishing:
            // Room-bound; an unplaced one is not assessed until it is given a room.
            return (component.parentRoomKeys?.value?.length ?? 0) > 0 ? 'surfaces' : null;
        case BaseType.Miscellaneous: {
            // Övrigt is assessed with the surface it is mounted on. Rooms win over a
            // stale parentSurface (as for utilities), so a room-bound one is not assessed.
            if ((component.parentRoomKeys?.value?.length ?? 0) > 0) return null;
            const parent = liveParent(component.parentSurface?.value, components);
            if (parent && MISCELLANEOUS_PARENT_TYPES.includes(parent.type)) {
                return resolveAssessmentGroup(parent, components, typeDefinitions);
            }
            return null;
        }
        default:
            return null;
    }
};

const componentsOf = (building: Building | null): ComponentMap =>
    (building?.model?.structure?.components as ComponentMap | undefined) ?? {};

/** Number of live components per group. Groups without components are absent. */
export const liveComponentCountsByGroup = (
    building: Building | null,
    typeDefinitions: TypeDefinitions
): Partial<Record<AssessmentGroupKey, number>> => {
    const components = componentsOf(building);
    const counts: Partial<Record<AssessmentGroupKey, number>> = {};
    for (const component of Object.values(components)) {
        if (!isLiveComponent(component)) continue;
        const group = resolveAssessmentGroup(component, components, typeDefinitions);
        if (group) counts[group] = (counts[group] ?? 0) + 1;
    }
    return counts;
};

/** Groups with at least one live component, in canonical order. */
export const groupsWithLiveComponents = (
    building: Building | null,
    typeDefinitions: TypeDefinitions
): AssessmentGroupKey[] => {
    const counts = liveComponentCountsByGroup(building, typeDefinitions);
    return ASSESSMENT_GROUP_KEYS.filter(group => (counts[group] ?? 0) > 0);
};

// ---------------------------------------------------------------------------
// Schedule resolution (active plan card and the activation dialog)
// ---------------------------------------------------------------------------

/**
 * A group's values as edited in the activation dialog; an entry exists only once the user
 * has edited the group. Held in the dialog and never stored: activation turns it into the
 * `inspections/active/{b}/conditionAssessment_{group}` instance.
 */
export interface AssessmentScheduleDraft {
    /** Proposed first due date; absent ⇒ activation date + effective interval. */
    nextDate?: string;
    /** Interval override; absent ⇒ template interval. */
    customInterval?: DateInterval;
}

export type AssessmentScheduleDrafts = Partial<Record<AssessmentGroupKey, AssessmentScheduleDraft>>;

/** The minimum of a check instance this module needs (`inspections/active/{b}/conditionAssessment_{group}`). */
export interface AssessmentInstanceLike {
    nextDueDate: string;
    customInterval?: DateInterval;
    lastCompletedAt?: string;
    lastCompletedBy?: string;
}

/** The minimum of a check template this module needs. */
export interface AssessmentTemplateLike {
    interval: DateInterval;
    description?: string;
}

/** A group's effective schedule, staged dialog values over the instance over the template (D16, D17). */
export interface AssessmentSchedule {
    group: AssessmentGroupKey;
    interval: DateInterval;
    /** YYYY-MM-DD */
    nextDueDate: string;
    /** True when `interval` comes from a staged or stored override rather than the template. */
    hasCustomInterval: boolean;
    /** True when `nextDueDate` was staged in the activation dialog. */
    hasStagedDate: boolean;
    /** True when the building has a check instance for the group. */
    isScheduled: boolean;
}


export interface ResolveAssessmentScheduleInput {
    group: AssessmentGroupKey;
    staged?: AssessmentScheduleDraft;
    instance?: AssessmentInstanceLike | null;
    template?: AssessmentTemplateLike | null;
    /** YYYY-MM-DD, the fallback base for `nextDueDate` when nothing is staged or stored. */
    today: string;
}

/**
 * Resolve one group's schedule. Null when the template is missing and there is
 * no instance: such a group draws no markers and shows "Mallar saknas".
 */
export const resolveAssessmentSchedule = ({ group, staged, instance, template, today }: ResolveAssessmentScheduleInput): AssessmentSchedule | null => {
    const interval = staged?.customInterval ?? instance?.customInterval ?? template?.interval ?? null;
    if (!interval) return null;
    const nextDueDate = staged?.nextDate ?? instance?.nextDueDate ?? addIntervalToDate(today, interval);
    return {
        group,
        interval,
        nextDueDate,
        hasCustomInterval: !!(staged?.customInterval ?? instance?.customInterval),
        hasStagedDate: !!staged?.nextDate,
        isScheduled: !!instance
    };
};
